import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Infinity as InfinityIcon, RotateCcw, ChevronRight, Handshake } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';

interface TenantRow {
  id: string; name: string; currency: string;
  plan: string; numbers: number; billing_exempt: boolean; plan_comp?: boolean;
  deletion_pending?: boolean; deletion_run_after?: string;
  signup_source?: string; partner_name?: string;
}

interface SourceRow {
  signup_source: string; partner_name?: string; accounts: number; paid: number;
  gross_cents: number; discount_cents: number; net_cents: number;
  courtesy_accounts: number; discount_pct: number;
}

/** Centavos → "R$ 1.234,56". A moeda do rate card é por conta; aqui o total é agregado,
 *  então mostramos em BRL, que é a moeda da esmagadora maioria. */
const reais = (cents: number) =>
  'R$ ' + (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PLAN_LABEL: Record<string, string> = { pro: 'Pro', free: 'Free', exempt: 'Cortesia' };

/** Como a conta entrou: cadastro direto, criada pelo admin ou trazida por um parceiro. */
function origem(t: { signup_source?: string; partner_name?: string }): { label: string; variant: 'secondary' | 'default' | 'outline' } {
  const src = t.signup_source ?? '';
  if (src.startsWith('connect:')) return { label: t.partner_name || src.slice(8), variant: 'default' };
  if (src === 'direct') return { label: 'Direto', variant: 'secondary' };
  if (src === 'admin') return { label: 'Admin', variant: 'outline' };
  return { label: '—', variant: 'outline' };
}

export function PlatformTenants() {
  const navigate = useNavigate();
  const tenants = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: async (): Promise<TenantRow[]> => ((await apiFetch('GET', '/platform/tenants')).data as { data?: TenantRow[] })?.data ?? [],
  });
  // Atribuição: quantas contas cada porta de entrada trouxe, e quantas delas pagam.
  const sources = useQuery({
    queryKey: ['platform-tenant-sources'],
    queryFn: async (): Promise<SourceRow[]> => ((await apiFetch('GET', '/platform/tenants/sources')).data as { data?: SourceRow[] })?.data ?? [],
  });

  const toggleExempt = async (t: TenantRow) => {
    const next = !t.billing_exempt;
    if (next && !window.confirm(`Tornar "${t.name}" CORTESIA? Acesso ilimitado e sem cobrança.`)) return;
    const r = await apiFetch('PATCH', `/platform/tenants/${t.id}/exempt`, { exempt: next });
    if (!r.ok) { toast.error('Falha ao alterar a cortesia.'); return; }
    toast.success(next ? 'Conta liberada (cortesia ilimitada).' : 'Cortesia removida.');
    tenants.refetch();
  };

  // Cancelar a exclusão agendada de uma conta (janela de arrependimento).
  const cancelDeletion = async (t: TenantRow) => {
    if (!window.confirm(`Cancelar a exclusão agendada de "${t.name}"? A conta volta a funcionar normalmente.`)) return;
    const r = await apiFetch('POST', `/platform/tenants/${t.id}/delete/cancel`);
    if (!r.ok) { toast.error('Falha ao cancelar a exclusão.'); return; }
    toast.success('Exclusão cancelada.');
    tenants.refetch();
  };

  // Reset p/ Free: cancela a assinatura no Stripe + zera plano/add-ons/carrinho. Limpar contas de teste.
  const resetPlan = async (t: TenantRow) => {
    if (!window.confirm(`Resetar "${t.name}" para o plano Free?\n\nCancela a assinatura no Stripe e zera add-ons/carrinho. Use para limpar contas de teste.`)) return;
    const r = await apiFetch('POST', `/platform/tenants/${t.id}/reset-plan`);
    if (!r.ok) { toast.error('Falha ao resetar a conta.'); return; }
    toast.success(`"${t.name}" resetada para Free.`);
    tenants.refetch();
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">Todas as contas da plataforma — origem, plano, números e cortesia. A cobrança é recorrente pelo Stripe.</p>

      {/* De onde vieram as contas. Parceiro aparece pelo nome; conta anterior ao
          rastreio fica em "Sem origem" — não dá para inventar de onde veio. */}
      {(sources.data ?? []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(sources.data ?? []).map((s) => {
            const o = origem(s);
            return (
              <div key={s.signup_source || 'none'} className="rounded-xl border bg-card px-4 py-2.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {s.signup_source.startsWith('connect:') ? <Handshake className="size-3.5" /> : null}
                  {s.signup_source ? o.label : 'Sem origem'}
                </div>
                <div className="mt-0.5 text-sm">
                  <span className="text-lg font-semibold tabular-nums">{s.accounts}</span>
                  <span className="text-muted-foreground"> conta{s.accounts === 1 ? '' : 's'} · {s.paid} pagando</span>
                </div>
                {/* O que a parceria rendeu e o que ela custou. Só faturas PAGAS: é
                    dinheiro que entrou, não projeção. */}
                {(s.net_cents > 0 || s.discount_cents > 0 || s.courtesy_accounts > 0) && (
                  <div className="mt-1.5 flex flex-col gap-0.5 border-t pt-1.5 text-xs">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">Recebido</span>
                      <span className="font-semibold tabular-nums">{reais(s.net_cents)}</span>
                    </div>
                    {s.discount_cents > 0 && (
                      <div className="flex items-center justify-between gap-4 text-muted-foreground">
                        <span>Desconto concedido</span>
                        <span className="tabular-nums">−{reais(s.discount_cents)}</span>
                      </div>
                    )}
                    {s.courtesy_accounts > 0 && (
                      <div className="flex items-center justify-between gap-4 text-muted-foreground">
                        <span>Em cortesia</span>
                        <span className="tabular-nums">{s.courtesy_accounts}</span>
                      </div>
                    )}
                    {s.discount_pct > 0 && (
                      <div className="text-muted-foreground">
                        {s.discount_pct === 100 ? 'Parceria isenta' : `Desconto vigente: ${s.discount_pct}%`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {tenants.isLoading ? <Skeleton className="h-40 w-full" /> : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Conta</th>
                  <th className="p-3 font-medium">Origem</th>
                  <th className="p-3 font-medium">Moeda</th>
                  <th className="p-3 font-medium">Plano</th>
                  <th className="p-3 text-right font-medium">Números</th>
                  <th className="p-3 font-medium">Cortesia</th>
                  <th className="p-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {(tenants.data ?? []).map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(t.id)}
                    className={`cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40${t.deletion_pending ? ' bg-destructive/5' : ''}`}
                    title="Ver detalhes da conta"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 font-medium">
                        <ChevronRight className="size-3.5 text-muted-foreground" />
                        {t.name || '—'}
                      </div>
                      <div className="pl-5 font-mono text-[0.65rem] text-muted-foreground">{t.id.slice(0, 8)}</div>
                      {t.deletion_pending && <Badge variant="destructive" className="ml-5 mt-1">Exclusão agendada</Badge>}
                    </td>
                    <td className="p-3"><Badge variant={origem(t).variant}>{origem(t).label}</Badge></td>
                    <td className="p-3"><Badge variant="secondary">{t.currency}</Badge></td>
                    <td className="p-3"><Badge variant={t.plan === 'pro' ? 'success' : 'secondary'}>{t.plan === 'pro' && t.plan_comp ? 'Pro cortesia' : (PLAN_LABEL[t.plan] ?? t.plan)}</Badge></td>
                    <td className="p-3 text-right tabular-nums">{t.numbers}</td>
                    {t.deletion_pending ? (
                      <td className="p-3" colSpan={2}>
                        <Button
                          size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); cancelDeletion(t); }}
                          title="Cancelar a exclusão agendada"
                        >
                          Cancelar exclusão
                        </Button>
                      </td>
                    ) : (
                      <>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant={t.billing_exempt ? 'default' : 'outline'}
                            onClick={(e) => { e.stopPropagation(); toggleExempt(t); }}
                            title={t.billing_exempt ? 'Conta cortesia (ilimitada). Clique para remover.' : 'Liberar como cortesia (ilimitado, sem cobrança).'}
                          >
                            {t.billing_exempt ? <><InfinityIcon className="size-4" /> Ilimitado</> : 'Liberar'}
                          </Button>
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm" variant="ghost"
                            className="text-muted-foreground hover:text-destructive"
                            disabled={t.billing_exempt}
                            onClick={(e) => { e.stopPropagation(); resetPlan(t); }}
                            title="Resetar para Free (cancela a assinatura no Stripe + zera add-ons)"
                          >
                            <RotateCcw className="size-4" /> Resetar
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      {!tenants.isLoading && (tenants.data ?? []).length === 0 && <Badge variant="secondary">Nenhuma conta.</Badge>}
    </div>
  );
}
